import * as THREE from 'three'
import { ATLAS_COLS, ATLAS_ROWS, SectionFrame, TILE_PX } from './types'

/**
 * One texture per mesh, tiled so each section owns a cell.
 *
 * V2 draws every section of a mesh into one canvas laid out as the unrolled surface, which
 * means the canvas can only be as expressive as the unwrap — and a section's pixels are
 * stretched by whatever the unwrap did to that part of the model. Here a tile is just the
 * section's own rectangle at a fixed resolution, so every section gets the same texel
 * density whatever it is sitting on, and adding a section never re-lays-out the others.
 */

export interface AtlasHandle {
  canvas: HTMLCanvasElement
  texture: THREE.CanvasTexture
  /** Side of one tile in pixels. Not baked into the shader — UVs are normalised. */
  tilePx: number
  dispose: () => void
}

/**
 * Draws one frame's content into its tile.
 *
 * The context arrives already translated to the tile's corner and clipped to it, so a
 * painter works in 0..size coordinates and cannot bleed into a neighbour.
 *
 * Injected because the two consumers paint completely different things: the admin portal
 * draws a label so an operator can see where a section is, while the Customizer draws the
 * customer's actual artwork. The tiling, the UV mapping and the shader are the parts that
 * must be identical; the pixels inside a tile are not.
 */
export type TilePainter = (ctx: CanvasRenderingContext2D, frame: SectionFrame, size: number) => void

export function createAtlas(options: { tilePx?: number } = {}): AtlasHandle {
  // 512 is ample for an authoring label and keeps the atlas at 2048x1024. Customer
  // artwork needs far more, so the consumer picks — a 4x2 grid at 1024 gives 4096x2048,
  // comfortably inside every GPU's limit.
  const tilePx = options.tilePx ?? TILE_PX
  const canvas = document.createElement('canvas')
  canvas.width = ATLAS_COLS * tilePx
  canvas.height = ATLAS_ROWS * tilePx

  const texture = new THREE.CanvasTexture(canvas)
  texture.colorSpace = THREE.SRGBColorSpace
  texture.wrapS = THREE.ClampToEdgeWrapping
  texture.wrapT = THREE.ClampToEdgeWrapping
  // Matches the glTF convention used everywhere else here, and is what v3Project's
  // downward-running v assumes.
  texture.flipY = false
  texture.minFilter = THREE.LinearFilter
  texture.magFilter = THREE.LinearFilter
  texture.generateMipmaps = false

  return {
    canvas,
    texture,
    tilePx,
    dispose: () => texture.dispose(),
  }
}

/**
 * Redraw every tile for one mesh.
 *
 * A section is painted as solid coverage with its label and border knocked back out of it,
 * because the shader reads the canvas as a MASK — alpha decides where the tint lands, and
 * the colour in the canvas is never seen. Drawing the label as a hole rather than as light
 * pixels is what makes it legible against a dark product as well as a pale one.
 *
 * EVERYTHING HERE IS LAID OUT IN MILLIMETRES, not tile pixels.
 *
 * A tile is a fixed square, and the shader stretches it across whatever the section's real
 * proportions are — so a 117 × 25 mm section stretches a square tile 4.7× horizontally and
 * every round number, letter and border drawn in tile pixels arrives on the product
 * smeared by that factor. Resizing a section then appears to deform it, which is a drawing
 * artefact and nothing to do with how the projection reaches the surface. Converting
 * through a separate pixels-per-millimetre for each axis cancels the stretch exactly: a
 * 2 mm border is 2 mm on both axes whatever shape the section is.
 */
export function drawAtlas(
  handle: AtlasHandle,
  frames: SectionFrame[],
  selectedId: string | null,
  paintTile: TilePainter = paintSectionLabel
): void {
  const ctx = handle.canvas.getContext('2d')
  if (!ctx) return

  const TILE = handle.tilePx
  ctx.clearRect(0, 0, handle.canvas.width, handle.canvas.height)

  frames.forEach((frame, index) => {
    const col = index % ATLAS_COLS
    const row = Math.floor(index / ATLAS_COLS)

    ctx.save()
    ctx.translate(col * TILE, row * TILE)
    ctx.beginPath()
    ctx.rect(0, 0, TILE, TILE)
    ctx.clip()
    paintTile(ctx, frame, TILE)
    ctx.restore()
  })

  // The selection ring is a shader uniform, not pixels, so selecting never re-rasterises.
  void selectedId

  handle.texture.needsUpdate = true
}

/**
 * The authoring tile: a coverage patch with the section's name, size and orientation cues
 * knocked out of it.
 */
export const paintSectionLabel: TilePainter = (ctx, frame, TILE_SIZE) => {
  {
    const TILE_PX = TILE_SIZE

    const wMm = Math.max(frame.width, 0.001)
    const hMm = Math.max(frame.height, 0.001)
    // The two scales that undo the stretch.
    const sx = TILE_PX / wMm
    const sy = TILE_PX / hMm
    const minMm = Math.min(wMm, hMm)

    // Coverage. Alpha is the whole payload — the colour here is never seen. Slightly
    // under 1 so the product's own surface still reads through the patch, which is how
    // an author tells a section on the stippled grip from one on the smooth frame.
    ctx.globalCompositeOperation = 'source-over'
    ctx.fillStyle = 'rgba(0, 0, 0, 0.82)'
    ctx.fillRect(0, 0, TILE_PX, TILE_PX)

    // Everything below cuts holes, so it reads as light on the engraved area.
    ctx.globalCompositeOperation = 'destination-out'
    ctx.fillStyle = '#ffffff'

    // Border: four bars, because a stroked rectangle cannot have a different thickness
    // per axis and an even thickness in millimetres is the whole point.
    const inset = Math.min(1.0, minMm * 0.06)
    const bar = Math.min(0.55, minMm * 0.035)
    const ix = inset * sx
    const iy = inset * sy
    const bx = Math.max(bar * sx, 1.5)
    const by = Math.max(bar * sy, 1.5)
    const innerW = TILE_PX - ix * 2
    const innerH = TILE_PX - iy * 2
    ctx.fillRect(ix, iy, innerW, by)
    ctx.fillRect(ix, TILE_PX - iy - by, innerW, by)
    ctx.fillRect(ix, iy, bx, innerH)
    ctx.fillRect(TILE_PX - ix - bx, iy, bx, innerH)

    // Corner ticks, so a section's orientation reads even when it carries no text.
    const tick = minMm * 0.18
    ctx.fillRect(ix, iy, tick * sx, by * 2.2)
    ctx.fillRect(ix, iy, bx * 2.2, tick * sy)

    // One arrow toward the section's +V: "which way is up" is otherwise a guess.
    const arrow = minMm * 0.12
    const cx = TILE_PX / 2
    ctx.save()
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.strokeStyle = '#ffffff'
    // Draw in millimetre space so the chevron keeps its shape on a long thin section.
    ctx.translate(cx, (inset + minMm * 0.16) * sy)
    ctx.scale(sx, sy)
    ctx.lineWidth = Math.max(bar, minMm * 0.028)
    ctx.beginPath()
    ctx.moveTo(-arrow, arrow * 0.6)
    ctx.lineTo(0, 0)
    ctx.lineTo(arrow, arrow * 0.6)
    ctx.stroke()
    ctx.restore()

    // Text is rasterised at its natural proportions, then placed at a millimetre size —
    // drawImage scales each axis independently, which is exactly the compensation needed.
    const titleMm = Math.min(hMm * 0.26, wMm * 0.2)
    drawTextMm(ctx, frame.name || 'Section', cx, TILE_PX * 0.44, titleMm, wMm * 0.82, sx, sy)

    const dims = `${round1(frame.width)} × ${round1(frame.height)} mm`
    drawTextMm(ctx, dims, cx, TILE_PX * 0.62, titleMm * 0.52, wMm * 0.7, sx, sy)

    if (frame.wrapRadius > 0) {
      drawTextMm(ctx, `wrap r${round1(frame.wrapRadius)}`, cx, TILE_PX * 0.78, titleMm * 0.42, wMm * 0.6, sx, sy)
    }
  }
}

/**
 * Place text so it measures `heightMm` tall and no more than `maxWidthMm` wide ON THE
 * PRODUCT, whatever the tile's stretch is. The glyphs are rendered square first and scaled
 * per axis on the way in, so they never inherit the section's aspect ratio.
 */
function drawTextMm(
  ctx: CanvasRenderingContext2D,
  text: string,
  cx: number,
  cy: number,
  heightMm: number,
  maxWidthMm: number,
  sx: number,
  sy: number
): void {
  if (!text || heightMm <= 0) return

  const REF = 160
  const scratch = document.createElement('canvas')
  const sctx = scratch.getContext('2d')
  if (!sctx) return

  const font = `bold ${REF}px Arial, sans-serif`
  sctx.font = font
  const metrics = sctx.measureText(text)
  const w = Math.ceil(metrics.width)
  const ascent = Math.ceil(metrics.actualBoundingBoxAscent || REF * 0.72)
  const descent = Math.ceil(metrics.actualBoundingBoxDescent || REF * 0.2)
  const h = ascent + descent
  if (w <= 0 || h <= 0) return

  scratch.width = w
  scratch.height = h
  sctx.font = font
  sctx.fillStyle = '#ffffff'
  sctx.fillText(text, 0, ascent)

  // Natural size in millimetres at the requested height, shrunk to fit the width budget.
  let drawHMm = heightMm
  let drawWMm = (w / h) * drawHMm
  if (drawWMm > maxWidthMm) {
    const k = maxWidthMm / drawWMm
    drawWMm *= k
    drawHMm *= k
  }

  const dw = drawWMm * sx
  const dh = drawHMm * sy
  ctx.drawImage(scratch, cx - dw / 2, cy - dh / 2, dw, dh)
}

function round1(n: number): number {
  return Math.round(n * 10) / 10
}
